import { Suspense, useEffect, useLayoutEffect } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { setRestConfig } from '@hydrogen/elements.core.rest.transport';
import { setGQLAuthToken, setGQLConfig } from '@hydrogen/elements.core.gql.transport';
import Router, { withBrowserRouter, setRouterConfigs } from '@hydrogen/elements.core.router';
import { useMode } from '@hydrogen/elements.ui.theme';
import { accessTokenSelector } from 'redux/auth/authSelectors';
import { Loader } from '@dekafvv/sharedfrontend';
import 'translations/i18n';
import { getCookie } from 'utils/storage';
import WithAuthentication from 'hocs/withAuthentication';
import { logout, autoLogout, autoRenew } from 'utils/jwtToken';
import { ErrorBoundary } from 'widgets/ErrorBoundary';
import { serviceConfig } from './configs/service.config';
import { routingConfig } from './configs/routing.config';
import { initConsentManager } from './utils/consentmanager';

setRestConfig(serviceConfig);
setRouterConfigs(routingConfig);
const axiosApiInstance = axios.create();

function App() {
    useMode();
    const tokenFromRedux: string = useSelector(accessTokenSelector);
    const token: string = decodeURI(getCookie('sinvest_jwt_token'));

    const navigate = useNavigate();

    useLayoutEffect(() => {
        if (token) {
            autoLogout(navigate);
            autoRenew(navigate);
            const memberAccessToken = token.split(' ')[1];

            axios.defaults.headers.common.Authorization = token;

            axios.interceptors.request.use(async (req: any) => {
                const sessionToken: string = decodeURI(getCookie('sinvest_jwt_token'));

                req.headers.Authorization = sessionToken;

                return req;
            });
            axios.interceptors.response.use(async (resp: any) => resp, async (error) => {
                const originalRequest = error.config;

                // eslint-disable-next-line no-underscore-dangle
                if (error.response?.status === 401 && !originalRequest._retry) {
                    const contactId = parseInt(getCookie('client_id'), 10);
                    const sessionToken: string = decodeURI(getCookie('sinvest_jwt_token'));

                    const sessionMemberAccessToken = sessionToken.split(' ')[1];

                    const respRenew = await fetch(
                        `${global?.AppConfig?.CUSTOM_URL_SUFFIX}/v1.0/contacts/${contactId}/authentication/tokens/renew`,
                        {
                            method: 'PUT',
                            headers: {
                                'Content-Type': 'application/json',
                            },
                            body: JSON.stringify({
                                accessToken: sessionMemberAccessToken,
                            }),
                        },

                    );

                    const renew = await respRenew.json();

                    if (renew?.result === 'Success') {
                        const bearerAccessToken = `Bearer ${renew.session.jwtAccessToken}`;

                        document.cookie = `sinvest_jwt_token=${bearerAccessToken};secure`;
                        document.cookie = `expiryDate=${renew.session.expiryDate};secure`;

                        // eslint-disable-next-line no-underscore-dangle
                        originalRequest._retry = true;
                        originalRequest.headers.Authorization = bearerAccessToken;

                        setGQLAuthToken(renew.session.jwtAccessToken);

                        return axiosApiInstance(originalRequest);
                    }
                    // eslint-disable-next-line no-underscore-dangle
                    originalRequest._retry = true;
                    logout(navigate);

                    return Promise.reject(error);
                }

                return Promise.reject(error);
            });

            // TODO check is it correct
            setGQLAuthToken(memberAccessToken);
            setGQLConfig(`${global.location.protocol}//${global.location.host}/`, 'graphql');
        } else {
            delete axios.defaults.headers.common.Authorization;
        }
    }, [tokenFromRedux]);

    useEffect(() => {
        initConsentManager('f504d21321565');
    }, []);

    return (
        <ErrorBoundary>
            <Suspense fallback={<Loader />}>
                <WithAuthentication>
                    <Router />
                </WithAuthentication>
            </Suspense>
        </ErrorBoundary>
    );
}

export default withBrowserRouter(App);
